import React, { useState, useEffect, useMemo } from 'react';
import { setToken } from "../../audit/authState";  // 설계 v7 §4-3 래퍼

// ──────────────────────────────────────────
// AdminSuperLogin.jsx — 슈퍼 로그인 (관리자 → 임의 사용자 가장)
// 레거시 /admin/superLogin 페이지 재현
// ──────────────────────────────────────────

const AdminSuperLogin = ({ API_BASE_URL, onImpersonate }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loginLoading, setLoginLoading] = useState(null); // userId being impersonated
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');

    // Fetch all users
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const token = localStorage.getItem('admin_token');
                const res = await fetch(`${API_BASE_URL}/api/auth/super-login/users`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (data.success) {
                    setUsers(data.data || []);
                } else {
                    setError(data.message || '사용자 목록 로드 실패');
                }
            } catch (e) {
                setError('서버 오류: ' + e.message);
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, [API_BASE_URL]);

    // Filtered users
    const filteredUsers = useMemo(() => {
        if (!search.trim()) return users;
        const q = search.toLowerCase();
        return users.filter(u =>
            (u.groupCode || '').toLowerCase().includes(q) ||
            (u.companyName || '').toLowerCase().includes(q) ||
            (u.userId || '').toLowerCase().includes(q) ||
            (u.userName || '').toLowerCase().includes(q)
        );
    }, [users, search]);

    // Impersonate user
    const handleImpersonate = async (user) => {
        const key = `${user.groupCode}_${user.userId}`;
        setLoginLoading(key);
        try {
            const token = localStorage.getItem('admin_token');
            const res = await fetch(`${API_BASE_URL}/api/auth/super-login/impersonate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ groupCode: user.groupCode, userId: user.userId }),
            });
            const data = await res.json();
            if (data.success) {
                // Set user session
                setToken(data.token, 'local');
                localStorage.setItem('groupCode', data.user.groupCode);
                localStorage.setItem('userId', data.user.userId);
                localStorage.setItem('superLogin', 'true');
                onImpersonate && onImpersonate(data);
            } else {
                alert(data.message || '로그인 실패');
            }
        } catch (e) {
            alert('서버 오류: ' + e.message);
        } finally {
            setLoginLoading(null);
        }
    };

    if (loading) {
        return (
            <div style={S.loadingWrap}>
                <div style={S.spinner} />
                <span style={{ color: '#6b7280', marginTop: 8 }}>사용자 목록 로딩 중...</span>
            </div>
        );
    }

    return (
        <div style={S.container}>
            <div style={S.headerRow}>
                <h2 style={S.title}>슈퍼 로그인</h2>
                <span style={S.badge}>{filteredUsers.length}명</span>
            </div>

            {error && <div style={S.errorBox}>{error}</div>}

            {/* Search */}
            <div style={S.searchWrap}>
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="업체코드, 회사명, 사용자ID로 검색..."
                    style={S.searchInput}
                />
                {search && (
                    <button onClick={() => setSearch('')} style={S.clearBtn}>✕</button>
                )}
            </div>

            {/* Table */}
            <div style={S.tableWrap}>
                <table style={S.table}>
                    <thead>
                        <tr style={S.thead}>
                            <th style={{ ...S.th, width: '40px' }}>No</th>
                            <th style={{ ...S.th, width: '80px' }}>업체코드</th>
                            <th style={{ ...S.th, width: '160px' }}>회사명</th>
                            <th style={{ ...S.th, width: '200px' }}>사용자 아이디</th>
                            <th style={{ ...S.th, width: '120px' }}>사용자 이름</th>
                            <th style={{ ...S.th, width: '80px' }}>로그인</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map((user, idx) => {
                            const key = `${user.groupCode}_${user.userId}`;
                            const isLogging = loginLoading === key;
                            return (
                                <tr key={key} style={idx % 2 === 0 ? S.rowEven : S.rowOdd}>
                                    <td style={S.td}>{idx + 1}</td>
                                    <td style={{ ...S.td, color: '#d97706', fontWeight: 600 }}>{user.groupCode}</td>
                                    <td style={S.td}>{user.companyName || '-'}</td>
                                    <td style={{ ...S.td, color: '#2563eb' }}>{user.userId}</td>
                                    <td style={S.td}>{user.userName || '-'}</td>
                                    <td style={S.td}>
                                        <button
                                            onClick={() => handleImpersonate(user)}
                                            disabled={isLogging}
                                            style={{
                                                ...S.loginBtn,
                                                opacity: isLogging ? 0.5 : 1,
                                            }}
                                        >
                                            {isLogging ? '...' : '로그인'}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {filteredUsers.length === 0 && (
                            <tr>
                                <td colSpan={6} style={{ ...S.td, textAlign: 'center', color: '#9ca3af', padding: '32px' }}>
                                    {search ? `"${search}" 검색 결과 없음` : '등록된 사용자가 없습니다.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const S = {
    container: { padding: '20px 24px', fontFamily: "'Malgun Gothic', '맑은 고딕', 'Pretendard Variable', -apple-system, sans-serif" },
    headerRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 700, color: '#1f2937', margin: 0 },
    badge: {
        background: '#dbeafe', color: '#2563eb', fontSize: 13, fontWeight: 600,
        padding: '2px 10px', borderRadius: 12,
    },
    errorBox: {
        padding: '10px', borderRadius: '8px', background: '#fef2f2',
        color: '#dc2626', border: '1px solid #fecaca', fontSize: '14px', marginBottom: 12,
    },
    searchWrap: { position: 'relative', marginBottom: 12, maxWidth: 400 },
    searchInput: {
        width: '100%', padding: '10px 32px 10px 12px', border: '1px solid #e5e7eb',
        borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
    },
    clearBtn: {
        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
        background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14,
    },
    tableWrap: { overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
    thead: { background: '#f9fafb' },
    th: {
        padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280',
        borderBottom: '2px solid #e5e7eb', fontSize: 13, whiteSpace: 'nowrap',
    },
    td: { padding: '8px 12px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' },
    rowEven: { background: '#fff' },
    rowOdd: { background: '#fafbfc' },
    loginBtn: {
        background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6,
        padding: '5px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        transition: 'background 0.15s',
    },
    loadingWrap: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '300px',
    },
    spinner: {
        width: 32, height: 32, border: '3px solid #e5e7eb',
        borderTopColor: '#2563eb', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
};

export default AdminSuperLogin;
